import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Info, X, Compass, Lightbulb } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

export type ContextType =
    | 'archaeology'
    | 'canon'
    | 'culture'
    | 'historicalEvents'
    | 'places'
    | 'book'
    | 'scripture'
    | 'literary'
    | 'engagementMethods'
    | 'inductive'
    | 'leaderToolkit';

interface IntroContent {
    title: string;
    description: string;
    mindset?: string;
}

const INTRO_CONTENT: Record<string, IntroContent> = {
    culture: {
        title: "The Audience Principle",
        mindset: "The Bible was written FOR us, but not TO us.",
        description: "We often miss the deeper meaning of a story because we don't share the same internal 'common sense' as the original audience. Understanding ancient values like honor, shame, and hospitality helps you feel the weight of every interaction the way it was first heard."
    },
    archaeology: {
        title: "Grounded Integrity",
        mindset: "Unearth the Evidence.",
        description: "Biblical events didn't happen in a vacuum - they happened in real dirt, stone, and seasons. Seeing the physical evidence reminds us that these were real people in a real world, giving the spiritual truths a solid foundation in history."
    },
    historicalEvents: {
        title: "Chronological Context",
        mindset: "Trace the Timeline.",
        description: "The Bible's message was often a response to real-world crises - invading empires, economic shifts, or political turmoil. Seeing what else was happening on the global stage explains the urgency and the 'why' behind the author's words."
    },
    places: {
        title: "Geographic Exploration",
        mindset: "Locate the Story.",
        description: "The physical terrain of the Bible wasn't just scenery; it was the 'classroom' where God shaped His people. Knowing the distance, the climb, and the climate turns names on a page into a three-dimensional experience of God's providence."
    },
    literary: {
        title: "Literary Design",
        mindset: "Identify Intentional Patterns.",
        description: "Biblical authors (human and divine) didn't just record information; they designed their message with artistic precision. Recognizing these literary patterns helps you spot the 'bolded' points and nuances that reveal the author's deepest intent."
    },
    book: {
        title: "Book Context",
        mindset: "See the Forest and the Trees.",
        description: "A single passage is like a single frame in a movie. Understanding where it fits within the whole book ensures you don't miss the overall 'plot' or accidentally take a promise or command out of its necessary context."
    },
    scripture: {
        title: "Scriptural Echoes",
        mindset: "Trace the Intertextual Web.",
        description: "The Bible is a vast, interconnected conversation where later authors constantly look back to earlier ones. Tracing these echoes reveals the consistency of God's character and the beautiful complexity of His unfolding plan."
    },
    engagementMethods: {
        title: "Formation over Information",
        mindset: "Beyond the Study.",
        description: "Information alone doesn't lead to transformation. These rhythms help you slow down and open your heart, moving biblical study from a purely intellectual exercise into a life-changing encounter with God."
    },
    inductive: {
        title: "The Expedition Path",
        mindset: "A Framework for Discovery.",
        description: "The amount of data in biblical study can be overwhelming. This journey provides a structured framework to help you move confidently from initial observation to meaningful, character-shaping application."
    },
    canon: {
        title: "The Ancient Library",
        mindset: "Understand the Literary Landscape.",
        description: "Every book in the Bible belongs to a specific 'neighborhood' within God's library. Understanding its genre and place in the canon sets your expectations correctly so you don't misinterpret a poetic song as a scientific report."
    },
    leaderToolkit: {
        title: "Group Facilitation",
        mindset: "Equip and Inspire.",
        description: "Leading a group is about more than having the right answers; it's about creating the right environment. This toolkit provides the hooks, discussion starters, and guidance you need to help others encounter the text for themselves."
    }
};

interface Props {
    type: ContextType;
    inline?: boolean;
}

export function ContextIntro({ type, inline = false }: Props) {
    const content = INTRO_CONTENT[type];
    const [isDismissed, setIsDismissed] = useState<boolean | null>(null);
    const [isExpanded, setIsExpanded] = useState(false);

    useEffect(() => {
        const stored = localStorage.getItem(`hasSeenIntro_${type}`);
        const dismissed = stored === 'true';
        setIsDismissed(dismissed);
        if (!dismissed && !inline) {
            setIsExpanded(true);
        } else {
            setIsExpanded(false);
        }
    }, [type, inline]);

    const handleDismissPermanently = () => {
        localStorage.setItem(`hasSeenIntro_${type}`, 'true');
        setIsDismissed(true);
        setIsExpanded(false);
    };

    if (!content || isDismissed === null || isDismissed === true) return null;

    // Inline mode - just show the button
    if (inline) {
        return (
            <div className="relative">
                <Button
                    variant="ghost"
                    size="sm"
                    className="h-10 items-center gap-2 rounded-full px-4 text-[13px] font-bold tracking-tight text-foreground/80 bg-primary/5 border border-primary/20 hover:bg-primary/10 hover:text-primary transition-all shadow-sm"
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    <Lightbulb className="h-4 w-4 text-primary" />
                    <span>Why?</span>
                </Button>

                <AnimatePresence>
                    {isExpanded && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 5 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 5 }}
                            className="absolute right-0 top-full z-50 mt-2 w-72 origin-top-right overflow-hidden rounded-xl border border-border bg-popover p-4 shadow-xl"
                        >
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-bold uppercase tracking-widest text-primary">{content.title}</span>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-5 w-5 rounded-full"
                                    onClick={() => setIsExpanded(false)}
                                >
                                    <X className="h-3 w-3" />
                                </Button>
                            </div>
                            {content.mindset && (
                                <p className="mb-2 text-[13px] font-semibold italic text-foreground/90">
                                    "{content.mindset}"
                                </p>
                            )}
                            <p className="text-[13px] leading-relaxed text-muted-foreground">
                                {content.description}
                            </p>
                            <div className="pt-4 flex flex-col gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-8 w-full rounded-full border-primary/20 bg-primary/5 hover:bg-primary/10 text-xs font-bold"
                                    onClick={() => setIsExpanded(false)}
                                >
                                    Got it
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-full rounded-full text-[10px] text-muted-foreground hover:text-destructive hover:bg-destructive/5"
                                    onClick={handleDismissPermanently}
                                >
                                    Stop showing this why button
                                </Button>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        );
    }

    // Full mode - show card
    return (
        <div className="mb-6">
            <AnimatePresence mode="wait">
                {isExpanded && (
                    <motion.div
                        key="bold-intro"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className="relative"
                    >
                        <Card className="overflow-hidden border-primary/20 bg-gradient-to-br from-primary/10 via-background to-background shadow-lg">
                            <CardContent className="p-5 pt-6">
                                <div className="flex gap-4">
                                    <div className="mt-1 flex-shrink-0">
                                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/20 text-primary">
                                            <Compass className="h-6 w-6" />
                                        </div>
                                    </div>
                                    <div className="flex-1 space-y-2">
                                        <div className="flex items-center justify-between">
                                            <h3 className="font-bold tracking-tight text-foreground">
                                                {content.title}
                                            </h3>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6 rounded-full"
                                                onClick={() => setIsExpanded(false)}
                                            >
                                                <X className="h-3 w-3" />
                                            </Button>
                                        </div>
                                        {content.mindset && (
                                            <p className="text-sm font-semibold italic text-primary/80">
                                                "{content.mindset}"
                                            </p>
                                        )}
                                        <p className="text-sm leading-relaxed text-muted-foreground">
                                            {content.description}
                                        </p>
                                        <div className="pt-3 flex items-center justify-between gap-4">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="h-8 px-6 rounded-full border-primary/20 bg-primary/5 hover:bg-primary/10 font-bold"
                                                onClick={() => setIsExpanded(false)}
                                            >
                                                Got it
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 text-[10px] text-muted-foreground hover:text-destructive"
                                                onClick={handleDismissPermanently}
                                            >
                                                Hide why button forever
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
